import React, { useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import { deleteTableRowAction } from 'components/spreadsheet/contexts/data/actions'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import Button from 'components/button'
import { getErrorSupportMetadata } from 'helpers/apiError'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'

interface DeleteRowModalProps extends ModalProps {
  rowIds: string[]
}

const DeleteRowModal: React.FC<DeleteRowModalProps> = ({ id, open, setOpen, rowIds }) => {
  const [processing, setProcessing] = useState<boolean>(false)

  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData, clearRowSelection } = useDataContext()

  const plural = rowIds.length !== 1

  const onSuccess = () => {
    clearRowSelection()
    setOpen(false)
    setProcessing(false)
    setSnackbarMessage({
      status: 'success',
      message: plural ? 'Rows successfully deleted.' : 'Row successfully deleted.'
    })
  }

  const onFailure = (error: unknown) => {
    setProcessing(false)
    // A routine rejection (a locked row, a missing permission) carries its own explanation;
    // only failures that need diagnosing get the generic message and the AI Support offer.
    const support = getErrorSupportMetadata(error)
    if (!support.offerSupport) {
      displayErrorMessage(error)
      return
    }
    setSnackbarMessage({
      status: 'error',
      message: plural
        ? "These rows couldn't be deleted. Refresh the page and try again."
        : "This row couldn't be deleted. Refresh the page and try again.",
      ...support
    })
  }

  const handleDeleteRows = () => {
    if (rowIds.length === 0) {
      setSnackbarMessage({ status: 'error', message: 'Select at least one row to delete.' })
      setOpen(false)
      return
    }

    setProcessing(true)
    deleteTableRowAction(rowIds, project.publicId, spreadsheetData, setSpreadsheetData, onSuccess, onFailure)
  }

  return (
    <Modal id={id} open={open} setOpen={setOpen} title="Confirm Deletion">
      {`Are you sure you want to delete ${plural ? `these ${rowIds.length} rows` : 'this row'}?`}

      <div className="flex justify-end mt-8">
        <Button internalType="secondary" className="mr-4" onClick={() => !processing && setOpen(false)}>
          Keep Row{plural ? 's' : ''}
        </Button>

        <Button
          internalType="danger"
          data-testid="confirm-delete-rows"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (processing) return
            handleDeleteRows()
          }}
          isLoading={processing}
          disabled={processing}
        >
          Delete Row{plural ? 's' : ''}
        </Button>
      </div>
    </Modal>
  )
}

export default DeleteRowModal
